import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
// Ensure this matches your file structure
import { salesOnboardingData } from "../data/salesOnboardingData";

import Timeline from "../components/framework/Timeline";

/* =========================
   TOKENS & GLOBAL STYLES
   ========================= */
const TokenStyles = () => (
  <style>{`
    :root {
      /* SALES BLUE THEME */
      --ink: #0f172a; 
      --ink-muted: #64748b; 
      --ink-light: #94a3b8;
      --bg-app: #f8fafc; 
      --bg-card: #ffffff;
      
      --brand: #2563eb;       /* Royal Blue */
      --brand-50: #eff6ff;    /* Very Light Blue */
      --brand-100: #dbeafe;
      
      --line: #e2e8f0; 
      --danger: #ef4444;
      
      --r-md: 8px; 
      --r-lg: 12px; 
      
      --sh-sm: 0 1px 2px rgba(0,0,0,.06);
      --sh-md: 0 4px 6px -1px rgba(0,0,0,.1), 0 2px 4px -2px rgba(0,0,0,.1);
      --sh-lg: 0 10px 15px -3px rgba(0,0,0,.1), 0 4px 6px -4px rgba(0,0,0,.1);
    }
    
    * { box-sizing: border-box; }
    
    body { margin: 0; background: var(--bg-app); color: var(--ink); font-family: ui-sans-serif, system-ui, -apple-system, sans-serif; }
    a { color: inherit; text-decoration: none; }
    
    /* Layout */
    .container { max-width: 1200px; margin: 0 auto; padding: 2.5rem 3rem; }
    @media (max-width: 768px) { .container { padding: 1.5rem; } }

    /* Cards */
    .card { background: var(--bg-card); border: 1px solid var(--line); border-radius: var(--r-lg); box-shadow: var(--sh-sm); padding: 1.5rem; margin-bottom: 1.5rem; transition: box-shadow .2s; }
    .card:hover { box-shadow: var(--sh-md); }
    
    /* Badges */
    .badge { display: inline-flex; align-items: center; padding: .25rem .6rem; border-radius: 999px; font-size: .75rem; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; white-space: nowrap; }
    .badge.brand { background: var(--brand-50); color: var(--brand); }
    .badge.outline { border: 1px solid var(--line); color: var(--ink-muted); }

    /* Typography */
    h1 { font-size: 2.25rem; margin: 0 0 .5rem; letter-spacing: -.02em; }
    h2 { font-size: 1.5rem; margin: 2.5rem 0 1rem; padding-bottom: .75rem; border-bottom: 1px solid var(--line); }
    h3 { font-size: 1.1rem; margin: .25rem 0 .5rem; font-weight: 600; }
    p { line-height: 1.6; color: var(--ink-muted); }

    /* Sticky IndexBar */
    .index-wrap { position: sticky; top: 0; z-index: 40; background: rgba(248,250,252,.9); backdrop-filter: blur(8px); border-bottom: 1px solid var(--line); }
    .index { display: flex; gap: .5rem; overflow: auto; padding: .75rem; scrollbar-width: none; }
    .index::-webkit-scrollbar { display: none; }
    
    /* Pills/Chips */
    .chip { border: 1px solid var(--line); border-radius: 999px; padding: .45rem .9rem; background: #fff; color: var(--ink-muted); font-size: .9rem; font-weight: 500; cursor: pointer; white-space: nowrap; transition: all .15s; }
    .chip:hover { transform: translateY(-1px); box-shadow: var(--sh-sm); color: var(--brand); }
    .chip[aria-current="true"] { background: var(--brand); color: #fff; border-color: var(--brand); }
    
    .index-shadow { box-shadow: 0 6px 10px -10px rgba(0,0,0,.25); }

    /* Skip Link */
    .skip { position: absolute; left: -9999px; top: auto; width: 1px; height: 1px; overflow: hidden; }
    .skip:focus { left: 1rem; top: 1rem; width: auto; height: auto; background: #fff; border: 1px solid var(--line); padding: .5rem .75rem; z-index: 999; }
  `}</style>
);

/* Helpers */
const Badge = ({ children, variant = "brand" }) => (
  <span className={`badge ${variant}`}>{children}</span>
);

const Card = ({ children, className = "", style }) => (
  <div className={`card ${className}`} style={style}>
    {children}
  </div>
);

const SafeList = ({ items }) => {
  if (!items?.length)
    return (
      <p
        className="text-sm"
        style={{ fontStyle: "italic", color: "var(--ink-light)" }}
      >
        No items available.
      </p>
    );
  return (
    <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
      {items.map((item, i) => (
        <li
          key={i}
          style={{
            display: "flex",
            gap: ".75rem",
            color: "var(--ink-muted)",
            marginBottom: ".5rem",
          }}
        >
          <span
            style={{
              color: "var(--brand)",
              fontSize: "1.2em",
              lineHeight: "1",
            }}
          >
            •
          </span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
};

/* Scroll-Spy Hook */
function useScrollSpy(ids) {
  const [active, setActive] = useState(ids[0]);
  const observerRef = useRef(null);

  useEffect(() => {
    const sections = ids
      .map((id) => document.getElementById(id))
      .filter(Boolean);
    if (observerRef.current) observerRef.current.disconnect();

    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: "-40% 0px -55% 0px", threshold: [0, 0.25, 0.5, 1] }
    );
    sections.forEach((el) => obs.observe(el));
    observerRef.current = obs;
    return () => obs.disconnect();
  }, [ids]);

  return [active, setActive];
}

/* =========================
   PAGE COMPONENT
   ========================= */
export default function SalesOnboardingPage() {
  const meta = salesOnboardingData?.meta || {};
  const strategy = salesOnboardingData?.strategy || {};
  const timeline = salesOnboardingData?.timeline || { phases: [] };
  const resources = salesOnboardingData?.resources || {};
  const risks = salesOnboardingData?.risks || { rows: [] };

  const [activeTab, setActiveTab] = useState("overview");

  const sections = useMemo(
    () => [
      { id: "overview", label: "Overview" },
      { id: "strategy", label: "Strategy" },
      { id: "timeline", label: "Timeline" },
      { id: "resources", label: "Resourcing" },
      { id: "risks", label: "Risks" },
    ],
    []
  );

  const ids = sections.map((s) => s.id);
  const [activeId] = useScrollSpy(ids);

  return (
    <>
      <TokenStyles />
      <a href="#overview" className="skip">
        Skip to content
      </a>

      {/* HEADER SECTION */}
      <div className="container" id="overview" style={{ paddingTop: "2rem" }}>
        <div style={{ marginBottom: "1rem" }}>
          <Badge>Framework V2.0</Badge>
        </div>
        <h1>{meta.title || "Sales Onboarding"}</h1>
        <p
          style={{
            fontSize: "1.1rem",
            color: "var(--ink-light)",
            maxWidth: "60ch",
          }}
        >
          {meta.subtitle}
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(250px,1fr))",
            gap: "1rem",
            marginTop: "1.5rem",
          }}
        >
          {(meta.outcomes ?? []).map((o, i) => (
            <Card key={i} style={{ marginBottom: 0 }}>
              <h3
                style={{
                  color: "var(--brand)",
                  marginTop: 0,
                  fontSize: "0.9rem",
                  textTransform: "uppercase",
                }}
              >
                Outcome {i + 1}
              </h3>
              <p style={{ margin: 0, fontSize: "0.95rem" }}>{o}</p>
            </Card>
          ))}
        </div>
      </div>

      {/* STICKY NAV BAR */}
      <div className={`index-wrap ${activeId ? "index-shadow" : ""}`}>
        <div
          className="container"
          style={{ paddingTop: ".5rem", paddingBottom: ".5rem" }}
        >
          <nav className="index" aria-label="Page navigation">
            {sections.map((s) => (
              <button
                key={s.id}
                className="chip"
                aria-current={activeId === s.id ? "true" : undefined}
                onClick={() =>
                  document
                    .getElementById(s.id)
                    ?.scrollIntoView({ behavior: "smooth" })
                }
              >
                {s.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* MAIN CONTENT AREA */}
      <main className="container">
        {/* 1. STRATEGY */}
        <section id="strategy" style={{ scrollMarginTop: "6rem" }}>
          <h2>Strategy & Methodology</h2>

          <div
            style={{ display: "flex", gap: ".5rem", marginBottom: "1.5rem" }}
          >
            {[
              { key: "overview", label: "Narrative" },
              { key: "sages", label: "Core Principles" },
              { key: "deliverables", label: "Deliverables" },
            ].map((t) => (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                className="chip"
                aria-current={activeTab === t.key ? "true" : undefined}
              >
                {t.label}
              </button>
            ))}
          </div>

          <Card>
            {activeTab === "overview" && (
              <div style={{ display: "grid", gap: "2rem" }}>
                {/* --- UPDATE: THE NORTH STAR QUOTE (Blue Pop) --- */}
                <div
                  style={{
                    borderLeft: "4px solid var(--brand)",
                    paddingLeft: "1.5rem",
                  }}
                >
                  <div
                    style={{
                      textTransform: "uppercase",
                      fontSize: "0.75rem",
                      fontWeight: "bold",
                      color: "var(--brand)",
                      marginBottom: "0.75rem",
                      letterSpacing: "0.05em",
                    }}
                  >
                    The North Star
                  </div>
                  <p
                    style={{
                      fontSize: "1.5rem",
                      lineHeight: "1.4",
                      fontWeight: "600",
                      margin: 0,
                      color: "var(--ink)",
                    }}
                  >
                    Success is not training completion. Success is{" "}
                    <strong style={{ color: "var(--brand)" }}>
                      Certified Autonomy
                    </strong>
                    .
                  </p>
                </div>

                <div
                  style={{
                    background: "var(--bg-app)",
                    padding: "1.5rem",
                    borderRadius: "8px",
                  }}
                >
                  <strong style={{ display: "block", marginBottom: "0.5rem" }}>
                    Key Pillars
                  </strong>
                  <SafeList items={strategy.tabs?.executive?.examples} />
                </div>
              </div>
            )}

            {activeTab === "sages" && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))",
                  gap: "2rem",
                }}
              >
                {(strategy.tabs?.sages?.sages ?? []).map((s, i) => (
                  <div key={i}>
                    <h4
                      style={{
                        color: "var(--brand)",
                        fontSize: "1.1rem",
                        margin: "0 0 0.5rem 0",
                      }}
                    >
                      {s.name}
                    </h4>
                    <p
                      style={{
                        fontSize: ".85rem",
                        fontStyle: "italic",
                        marginBottom: "1rem",
                      }}
                    >
                      {s.philosophy}
                    </p>
                    <SafeList items={s.bullets} />
                  </div>
                ))}
              </div>
            )}

            {activeTab === "deliverables" && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "2rem",
                }}
              >
                <div>
                  <h4
                    style={{
                      color: "var(--ink)",
                      borderBottom: "1px solid var(--line)",
                      paddingBottom: "0.5rem",
                    }}
                  >
                    Certification Artifacts
                  </h4>
                  <SafeList
                    items={strategy.tabs?.deliverables?.certificationArtifacts}
                  />
                </div>
                <div>
                  <h4
                    style={{
                      color: "var(--ink)",
                      borderBottom: "1px solid var(--line)",
                      paddingBottom: "0.5rem",
                    }}
                  >
                    Core Materials
                  </h4>
                  <SafeList
                    items={strategy.tabs?.deliverables?.coreArtifacts}
                  />
                </div>
              </div>
            )}
          </Card>
        </section>

        {/* 2. TIMELINE */}
        <section id="timeline" style={{ scrollMarginTop: "6rem" }}>
          <h2>Visual Timeline</h2>
          <Timeline items={timeline.phases} />
        </section>

        {/* 3. RESOURCING */}
        <section id="resources" style={{ scrollMarginTop: "6rem" }}>
          <h2>Resourcing</h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))",
              gap: "1.5rem",
            }}
          >
            <Card>
              <h4
                style={{
                  color: "var(--ink-muted)",
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  fontSize: ".8rem",
                }}
              >
                Minimal Viable Team
              </h4>
              <h3 style={{ marginTop: 0 }}>Starter Squad</h3>
              <SafeList items={resources.minimumViableTeam?.items} />
            </Card>
            <Card
              style={{
                background: "var(--brand-50)",
                borderColor: "var(--brand)",
              }}
            >
              <h4
                style={{
                  color: "var(--brand)",
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  fontSize: ".8rem",
                }}
              >
                Ideal State
              </h4>
              <h3 style={{ marginTop: 0, color: "var(--brand)" }}>
                Scale Team
              </h3>
              <SafeList items={resources.idealTeam?.items} />
            </Card>
          </div>
        </section>

        {/* 4. RISKS */}
        <section
          id="risks"
          style={{ scrollMarginTop: "6rem", paddingBottom: "3rem" }}
        >
          <h2>Risk Analysis</h2>
          <Card style={{ padding: 0, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead
                style={{
                  background: "var(--bg-app)",
                  borderBottom: `1px solid var(--line)`,
                }}
              >
                <tr>
                  <th
                    style={{
                      textAlign: "left",
                      padding: "1rem",
                      fontSize: ".85rem",
                      textTransform: "uppercase",
                      color: "var(--ink-muted)",
                    }}
                  >
                    Risk Factor
                  </th>
                  <th
                    style={{
                      textAlign: "left",
                      padding: "1rem",
                      fontSize: ".85rem",
                      textTransform: "uppercase",
                      color: "var(--ink-muted)",
                    }}
                  >
                    Mitigation Strategy
                  </th>
                </tr>
              </thead>
              <tbody>
                {(risks.rows ?? []).map((row, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid var(--line)` }}>
                    <td
                      style={{
                        padding: "1rem",
                        verticalAlign: "top",
                        color: "var(--danger)",
                        fontWeight: 600,
                        width: "35%",
                      }}
                    >
                      {row.risk}
                    </td>
                    <td
                      style={{
                        padding: "1rem",
                        verticalAlign: "top",
                        color: "var(--ink-muted)",
                      }}
                    >
                      {row.mitigation}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!(risks.rows ?? []).length && (
              <div style={{ padding: "1rem" }}>No risks identified.</div>
            )}
          </Card>
        </section>
      </main>
    </>
  );
}
